'use client';

import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';

import {
    getDeliveryType,
    logSentryError,
    SentryAcpUseInventoriesErrors as SentryErrors,
    useCart,
    useDecisions,
} from '@libs/common-next';
import type { AccountProduct, Inventory, ItemUI } from '@libs/common-node';
import { AccountProductStatus } from '@libs/common-types';
import { DeliveryType, LOCALIZED } from '@libs/core';

import type { OrderLine } from '@/components/account/OrderDetails/types';
import { FetchInventoryList, fetchInventoryList } from '@/utils';
import { getCaseRequiredQuantity } from '@/utils/ordersUtils';

type InventoryHookResult = {
    inventories?: Inventory[];
    isLoading: boolean;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    fetchInventories: (orderLine?: OrderLine[]) => Promise<void>;
    fetchInventoriesBySkuStorePairs: (skuStorePairs: FetchInventoryList) => Promise<void>;
    getProductQuantity: (product: AccountProduct, orderedQuantity?: string, fulfillmentMethod?: string) => number;
};

const inventoryListId = LOCALIZED.webInventory;

export function useInventories(initialInventories?: Inventory[]): InventoryHookResult {
    const { cart } = useCart();
    const [inventories, setInventories] = useState<Inventory[] | undefined>(initialInventories);
    const [isLoading, setIsLoading] = useState(false);
    const newInvCheckFlag = useDecisions('ext_inv_check');
    const getAdjustedFulfillmentMethod = (product: AccountProduct, fulfillmentMethod?: string) =>
        newInvCheckFlag && product.status === AccountProductStatus.addToCartSTH ? DeliveryType.STH : fulfillmentMethod;

    const fallbackInventoryCheck = (product: AccountProduct, orderedQuantity?: string, fulfillmentMethod?: string) => {
        const { isCaseRequired, customCaseQuantity = 1, bopisCaseQty = 1, sku, storeId } = product;

        const inventory = inventories?.find(item => item.sku === sku);
        const storeKey =
            newInvCheckFlag && product.status === AccountProductStatus.addToCartSTH
                ? inventoryListId
                : (storeId ?? inventoryListId);
        const adjustedFulfillmentMethod = getAdjustedFulfillmentMethod(product, fulfillmentMethod);
        const qtyInStock = inventory ? Number(inventory[storeKey]) || 0 : 0;
        const orderedQuantityNumber = Number(orderedQuantity);

        if (qtyInStock === 0) return 0;

        if (isCaseRequired) {
            const { isBopis } = getDeliveryType(adjustedFulfillmentMethod);
            const caseQty = Number(isBopis ? (bopisCaseQty ?? customCaseQuantity) : customCaseQuantity);

            if (qtyInStock < caseQty) return 0;
            return orderedQuantityNumber >= caseQty && orderedQuantityNumber <= qtyInStock
                ? orderedQuantityNumber
                : caseQty; // Return minimum case quantity instead of full stock
        }

        return orderedQuantityNumber <= qtyInStock ? orderedQuantityNumber : 1; // Return 1 instead of full stock for regular products
    };

    const fetchInventories = async (orderLines?: OrderLine[]) => {
        try {
            if (!orderLines) {
                return;
            }
            const skuStorePairs = orderLines.map(order => ({
                sku: order.product.sku,
                store:
                    newInvCheckFlag && order.product.status === AccountProductStatus.addToCartSTH
                        ? LOCALIZED.webInventory
                        : (order.shippingInfo?.shipToFacility ?? LOCALIZED.webInventory),
            }));

            setIsLoading(true);
            const result = await fetchInventoryList(skuStorePairs);
            setInventories(result);
        } catch (err) {
            logSentryError(err, SentryErrors.useInventories.fetchInventoriesBySkuStorePairs);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const fetchInventoriesBySkuStorePairs = async (skuStorePairs: FetchInventoryList) => {
        try {
            if (!skuStorePairs) {
                return;
            }

            setIsLoading(true);
            const result = await fetchInventoryList(skuStorePairs);
            setInventories(result);
        } catch (error) {
            logSentryError(error, SentryErrors.useInventories.fetchInventoriesBySkuStorePairs);
        } finally {
            setIsLoading(false);
        }
    };

    const getProductQuantity = (product: AccountProduct, orderedQuantity?: string, fulfillmentMethod?: string) => {
        const requestedQty = Number(orderedQuantity);
        const { isCaseRequired, customCaseQuantity = 1, bopisCaseQty, sku } = product;

        if (cart?.id) {
            const newItem = cart?.fulfillmentGroups
                .reduce((items, group) => [...items, ...group.items], [] as ItemUI[])
                .find(item => item?.sku === sku);

            const qtyInCart = newItem?.quantity;
            const qtyInStock = newItem?.availableQuantity;

            if (qtyInCart === undefined || qtyInStock === undefined) {
                return fallbackInventoryCheck(product, orderedQuantity, fulfillmentMethod);
            }

            const remainingStock = qtyInStock - qtyInCart;

            if (remainingStock <= 0) return 0;

            if (isCaseRequired) {
                const adjustedFulfillmentMethod = getAdjustedFulfillmentMethod(product, fulfillmentMethod);
                const { isBopis } = getDeliveryType(adjustedFulfillmentMethod);
                const caseQty = isBopis ? (bopisCaseQty ?? customCaseQuantity) : customCaseQuantity;
                return getCaseRequiredQuantity(remainingStock, requestedQty, caseQty);
            }

            return requestedQty <= remainingStock ? requestedQty : remainingStock;
        }

        return fallbackInventoryCheck(product, orderedQuantity, fulfillmentMethod);
    };

    return {
        inventories,
        fetchInventories,
        fetchInventoriesBySkuStorePairs,
        getProductQuantity,
        isLoading,
        setIsLoading,
    };
}
